/**
 * <quick-shop>
 *
 * Always-visible product-card quick shop: custom listbox + ATC.
 * The native <select> is the data source; listbox changes sync its value and
 * dispatch `change` for Quick Shop + Searchspring handlers.
 *
 * The options panel is portaled to document.body (position: fixed) while open so
 * it escapes card/carousel overflow and stacking contexts (e.g. product grid /
 * footer). Never resolve the list via getElementById — recommendation sections
 * duplicate desktop/mobile markup with shared IDs.
 */

const WRAPPER_SELECTOR = '.quick-shop__select-wrapper:not(.quick-shop__select-wrapper--static)';
const OPEN_CLASS = 'is-open';
const FIXED_CLASS = 'is-fixed';
const OPTIONS_OFFSET_PX = 7;
const OPTIONS_WIDTH_RATIO = 0.85;
const VIEWPORT_PAD_PX = 8;

/** @type {WeakMap<HTMLElement, HTMLElement>} */
const listToWrapper = new WeakMap();
/** @type {WeakMap<HTMLElement, HTMLElement>} */
const wrapperToList = new WeakMap();

/** @type {HTMLElement | null} */
let openWrapper = null;
/** @type {number | null} */
let positionFrame = null;

/**
 * @typedef {{
 *   wrapper: HTMLElement,
 *   select: HTMLSelectElement,
 *   trigger: HTMLButtonElement,
 *   list: HTMLElement,
 *   label: HTMLElement | null,
 * }} DropdownParts
 */

/** @param {HTMLElement} wrapper */
function getListForWrapper(wrapper) {
  return wrapper.querySelector(':scope > .quick-shop__select-options')
    ?? wrapperToList.get(wrapper)
    ?? null;
}

/**
 * @param {HTMLElement} wrapper
 * @returns {DropdownParts | null}
 */
function getParts(wrapper) {
  const select = wrapper.querySelector('.quick-shop__select');
  const trigger = wrapper.querySelector('.quick-shop__select-trigger');
  const list = getListForWrapper(wrapper);

  if (
    !(select instanceof HTMLSelectElement)
    || !(trigger instanceof HTMLButtonElement)
    || !(list instanceof HTMLElement)
  ) {
    return null;
  }

  return {
    wrapper,
    select,
    trigger,
    list,
    label: wrapper.querySelector('.quick-shop__select-label'),
  };
}

/**
 * @param {EventTarget | null | undefined} target
 * @returns {DropdownParts | null}
 */
function getPartsFromEventTarget(target) {
  if (!(target instanceof Element)) return null;

  const list = target.closest('.quick-shop__select-options');
  if (list instanceof HTMLElement) {
    const wrapper = listToWrapper.get(list);
    if (wrapper) return getParts(wrapper);
  }

  const wrapper = target.closest(WRAPPER_SELECTOR);
  return wrapper instanceof HTMLElement ? getParts(wrapper) : null;
}

/** @param {HTMLElement} list */
function clearFixedStyles(list) {
  list.classList.remove(FIXED_CLASS);
  list.style.removeProperty('--quick-shop-options-top');
  list.style.removeProperty('--quick-shop-options-right');
  list.style.removeProperty('--quick-shop-options-width');

  const colorClass = list.dataset.portedColorClass;
  if (colorClass) {
    list.classList.remove(colorClass);
    delete list.dataset.portedColorClass;
  }
}

/**
 * Copy the nearest color-scheme class once so portaled panels keep card colors.
 * @param {DropdownParts} parts
 */
function syncPortaledColorScheme(parts) {
  if (parts.list.dataset.portedColorClass) return;

  const host = parts.wrapper.closest('quick-shop') ?? parts.wrapper.closest('[class*="color-"]');
  if (!host) return;

  for (const cls of host.classList) {
    if (!cls.startsWith('color-')) continue;
    parts.list.classList.add(cls);
    parts.list.dataset.portedColorClass = cls;
    break;
  }
}

/**
 * @param {DropdownParts} parts
 * @param {boolean} [portal=false]
 */
function positionList(parts, portal = false) {
  const rect = parts.trigger.getBoundingClientRect();
  if (!rect.width && !rect.height) {
    setOpen(parts.wrapper, false);
    return;
  }

  listToWrapper.set(parts.list, parts.wrapper);
  wrapperToList.set(parts.wrapper, parts.list);

  if (portal || parts.list.parentElement !== document.body) {
    document.body.appendChild(parts.list);
    syncPortaledColorScheme(parts);
  }

  const width = Math.round(rect.width * OPTIONS_WIDTH_RATIO);
  const right = Math.max(VIEWPORT_PAD_PX, Math.round(window.innerWidth - rect.right));
  let top = Math.round(rect.bottom + OPTIONS_OFFSET_PX);

  parts.list.classList.add(FIXED_CLASS);
  parts.list.style.setProperty('--quick-shop-options-width', `${width}px`);
  parts.list.style.setProperty('--quick-shop-options-right', `${right}px`);
  parts.list.style.setProperty('--quick-shop-options-top', `${top}px`);

  const height = parts.list.getBoundingClientRect().height || parts.list.scrollHeight;
  const fitsBelow = top + height <= window.innerHeight - VIEWPORT_PAD_PX;
  const fitsAbove = rect.top - OPTIONS_OFFSET_PX - height >= VIEWPORT_PAD_PX;

  if (!fitsBelow && fitsAbove) {
    top = Math.round(rect.top - OPTIONS_OFFSET_PX - height);
  } else if (!fitsBelow) {
    top = Math.max(VIEWPORT_PAD_PX, Math.round(window.innerHeight - VIEWPORT_PAD_PX - height));
  }

  parts.list.style.setProperty('--quick-shop-options-top', `${top}px`);
}

/** @param {DropdownParts} parts */
function restoreList(parts) {
  clearFixedStyles(parts.list);
  listToWrapper.delete(parts.list);
  wrapperToList.delete(parts.wrapper);

  if (parts.list.parentElement !== parts.wrapper) {
    parts.wrapper.appendChild(parts.list);
  }
}

/**
 * @param {HTMLElement} wrapper
 * @param {boolean} open
 */
function setOpen(wrapper, open) {
  const parts = getParts(wrapper);
  if (!parts) return;

  wrapper.classList.toggle(OPEN_CLASS, open);
  parts.trigger.setAttribute('aria-expanded', String(open));
  parts.list.hidden = !open;

  const card = wrapper.closest('product-card, .ss-recommendations__card');
  card?.classList.toggle('has-open-variant-dropdown', open);

  if (open) {
    if (openWrapper && openWrapper !== wrapper) setOpen(openWrapper, false);
    openWrapper = wrapper;
    positionList(parts, true);
    return;
  }

  if (openWrapper === wrapper) openWrapper = null;
  restoreList(parts);
}

/** @param {HTMLElement} [except] */
function closeAll(except) {
  if (openWrapper && openWrapper !== except) setOpen(openWrapper, false);
}

/** @param {DropdownParts} parts */
function toggle(parts) {
  const willOpen = !parts.wrapper.classList.contains(OPEN_CLASS);
  closeAll(willOpen ? parts.wrapper : undefined);
  setOpen(parts.wrapper, willOpen);

  if (!willOpen) return;

  const selected = parts.list.querySelector('.quick-shop__select-option.is-selected');
  if (selected instanceof HTMLElement) selected.focus();
}

/**
 * @param {DropdownParts} parts
 * @param {HTMLElement} optionEl
 */
function selectOption(parts, optionEl) {
  const value = optionEl.dataset.value;
  if (!value) return;

  parts.select.value = value;

  for (const opt of parts.list.querySelectorAll('.quick-shop__select-option')) {
    const selected = opt === optionEl;
    opt.classList.toggle('is-selected', selected);
    opt.setAttribute('aria-selected', String(selected));
  }

  if (parts.label) parts.label.textContent = optionEl.textContent?.trim() ?? '';

  setOpen(parts.wrapper, false);
  parts.select.dispatchEvent(new Event('change', { bubbles: true }));
  parts.trigger.focus();
}

/**
 * @param {DropdownParts} parts
 * @param {number} direction
 */
function moveHighlight(parts, direction) {
  if (parts.list.hidden) return;

  /** @type {HTMLElement[]} */
  const options = [];
  for (const node of parts.list.querySelectorAll('.quick-shop__select-option')) {
    if (node instanceof HTMLElement) options.push(node);
  }
  if (!options.length) return;

  const marked = parts.list.querySelector(
    '.quick-shop__select-option.is-active, .quick-shop__select-option.is-selected',
  );
  const currentIndex = marked instanceof HTMLElement ? options.indexOf(marked) : -1;
  const fromIndex = currentIndex >= 0 ? currentIndex : 0;
  const nextIndex = Math.max(0, Math.min(options.length - 1, fromIndex + direction));
  const next = options[nextIndex];
  if (!next) return;

  for (const [index, opt] of options.entries()) {
    opt.classList.toggle('is-active', index === nextIndex);
  }
  next.focus();
}

/** @param {DropdownParts} parts */
function ensureOpen(parts) {
  if (parts.wrapper.classList.contains(OPEN_CLASS)) return;
  closeAll(parts.wrapper);
  setOpen(parts.wrapper, true);
}

function scheduleReposition() {
  if (!openWrapper || positionFrame !== null) return;

  positionFrame = requestAnimationFrame(() => {
    positionFrame = null;
    if (!openWrapper) return;
    const parts = getParts(openWrapper);
    if (parts) positionList(parts);
  });
}

/** @param {MouseEvent} event */
function onDocumentClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const optionEl = target.closest('.quick-shop__select-option');
  if (optionEl instanceof HTMLElement) {
    event.preventDefault();
    event.stopPropagation();
    const parts = getPartsFromEventTarget(optionEl);
    if (parts) selectOption(parts, optionEl);
    return;
  }

  const trigger = target.closest('.quick-shop__select-trigger');
  if (trigger instanceof HTMLButtonElement) {
    event.preventDefault();
    event.stopPropagation();
    const parts = getPartsFromEventTarget(trigger);
    if (parts) toggle(parts);
    return;
  }

  if (target.closest(`${WRAPPER_SELECTOR}, .quick-shop__select-options`)) {
    event.stopPropagation();
    return;
  }

  closeAll();
}

/** @param {KeyboardEvent} event */
function onDocumentKeydown(event) {
  const parts = getPartsFromEventTarget(event.target);
  if (!parts) return;

  const onTrigger = event.target === parts.trigger || parts.trigger.contains(/** @type {Node} */ (event.target));
  const optionEl = event.target instanceof Element
    ? event.target.closest('.quick-shop__select-option')
    : null;
  const isOpen = parts.wrapper.classList.contains(OPEN_CLASS);

  switch (event.key) {
    case 'Escape':
      if (!isOpen) return;
      event.preventDefault();
      setOpen(parts.wrapper, false);
      parts.trigger.focus();
      break;

    case 'ArrowDown':
    case 'ArrowUp':
      event.preventDefault();
      if (onTrigger) ensureOpen(parts);
      moveHighlight(parts, event.key === 'ArrowDown' ? 1 : -1);
      break;

    case 'Enter':
    case ' ':
      if (optionEl instanceof HTMLElement) {
        event.preventDefault();
        selectOption(parts, optionEl);
      } else if (onTrigger) {
        event.preventDefault();
        toggle(parts);
      }
      break;

    case 'Tab':
      if (isOpen) setOpen(parts.wrapper, false);
      break;

    default:
      break;
  }
}

/** @param {Event} event */
function onViewportMove(event) {
  if (!openWrapper) return;
  if (event.target instanceof Element && event.target.closest('.quick-shop__select-options')) return;
  scheduleReposition();
}

document.addEventListener('click', onDocumentClick, true);
document.addEventListener('keydown', onDocumentKeydown);
document.addEventListener('scroll', onViewportMove, { capture: true, passive: true });
window.addEventListener('resize', onViewportMove, { passive: true });

class QuickShop extends HTMLElement {
  /** @type {HTMLSelectElement | null} */
  select = null;

  /** @type {HTMLInputElement | null} */
  variantIdInput = null;

  connectedCallback() {
    this.select = this.querySelector('.quick-shop__select');
    this.variantIdInput = this.querySelector('input[name="id"]');

    if (!this.select) return;

    this.select.addEventListener('change', this.#onChange);
    // Sync initial variant price (cards otherwise show a range). Skip image — gallery is already correct.
    const initial = this.select.options[this.select.selectedIndex];
    if (initial) this.#update(initial, { updateImage: false });
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.#onChange);

    const wrapper = this.querySelector(WRAPPER_SELECTOR);
    if (wrapper instanceof HTMLElement && openWrapper === wrapper) setOpen(wrapper, false);
  }

  #onChange = () => {
    const option = this.select?.options[this.select.selectedIndex];
    if (option) this.#update(option, { updateImage: true });
  };

  /**
   * @param {HTMLOptionElement} option
   * @param {{ updateImage: boolean }} opts
   */
  #update(option, { updateImage }) {
    if (!option) return;

    const available = option.dataset.available === 'true';

    // 1) Keep the add-to-cart form pointed at the selected variant.
    if (this.variantIdInput) {
      this.variantIdInput.value = option.dataset.variantId || option.value || '';
      this.variantIdInput.disabled = !available;
    }

    // 2) Enable/disable the add-to-cart button.
    const atcButton = this.querySelector('.quick-shop__atc');
    if (atcButton instanceof HTMLButtonElement) {
      atcButton.disabled = !available;
    }

    const card = this.closest('product-card');
    if (!card) return;

    // 3) Update the card price.
    this.#updatePrice(card, option);

    // 4) Update the card image (only on user interaction).
    if (updateImage) this.#updateImage(card, option, true);
  }

  /**
   * Updates the visible price for a variant.
   *
   * IMPORTANT (iOS Safari): Do not reuse/update the same text node after the parent slideshow has been arrow-navigated.
   * Safari keeps stale glyph paint for product-price inside slideshow-slides (view-timeline / content-visibility).
   * Always swap in a fresh price element and keep only one visible price.
   *
   * @param {Element} card
   * @param {HTMLOptionElement} option
   */
  #updatePrice(card, option) {
    const productPrice = card.querySelector('product-price');
    const container = productPrice?.querySelector("[ref='priceContainer']");
    if (!container) return;

    const price = option.dataset.price || '';
    const compareAt = option.dataset.compareAtPrice || '';
    const onSale = option.dataset.onSale === 'true' && Boolean(compareAt);

    const next = document.createElement('div');
    next.setAttribute('ref', 'priceContainer');

    if (onSale) {
      next.innerHTML =
        '<div class="price__regular price__hidden"><span class="price"></span></div>' +
        '<div class="price__sale">' +
        '<span class="price-item__group"><span class="price-item--regular compare-at-price"></span></span>' +
        '<span class="price-item__group"><span class="price-item--sale price"></span></span>' +
        '</div>';
      const compareEl = next.querySelector('.compare-at-price');
      const saleEl = next.querySelector('.price-item--sale');
      if (compareEl) compareEl.textContent = compareAt;
      if (saleEl) saleEl.textContent = price;
    } else {
      next.innerHTML = '<div class="price__regular"><span class="price"></span></div>';
      const priceEl = next.querySelector('.price');
      if (priceEl) priceEl.textContent = price;
    }

    // Fresh node forces Safari to drop prior painted price layers.
    container.replaceWith(next);

    // Nudge a paint of the host if the browser kept a detach layer on product-price.
    if (productPrice instanceof HTMLElement) {
      productPrice.style.transform = 'translateZ(0)';
      // Force reflow, then clear the temporary compositing hint.
      void productPrice.offsetHeight;
      productPrice.style.removeProperty('transform');
    }
  }

  /**
   * @param {Element} card
   * @param {HTMLOptionElement} option
   * @param {boolean} animate
   */
  #updateImage(card, option, animate) {
    const imageUrl = option.dataset.image;
    if (!imageUrl) return;

    const gallery = card.querySelector('.card-gallery');
    const image = gallery?.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;

    // Compare against the raw path (protocol/size params vary) to avoid needless swaps.
    const nextPath = imageUrl.split('?')[0];
    if (image.currentSrc && image.currentSrc.split('?')[0] === nextPath) return;

    const apply = () => {
      image.srcset = imageUrl;
      image.src = imageUrl;
    };

    if (animate && typeof image.animate === 'function') {
      const fade = image.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 200, easing: 'ease-in-out' });
      apply();
      fade.play();
    } else {
      apply();
    }
  }
}

if (!customElements.get('quick-shop')) {
  customElements.define('quick-shop', QuickShop);
}
